// src/policies/PolicyStore.js

/**
 * A tiny "policy store" class.
 * Today: returns default local policy data.
 * Later: replace implementation to fetch from DB/API, without touching UI pages.
 */

export class PolicyStore {
  constructor({ source = "local" } = {}) {
    this.source = source;
  }

  /**
   * Return a PolicyDocument object
   * @param {"privacy"|"terms"} kind
   */
  async getPolicy(kind) {
    // Local fallback (default)
    if (this.source === "local") {
      return kind === "privacy" ? DEFAULT_PRIVACY_POLICY : DEFAULT_TERMS_POLICY;
    }

    // Future: DB/API fetch
    // Example:
    // if (this.source === "remote") return fetchPolicyFromApi(kind);
    throw new Error(`Unknown policy source: ${this.source}`);
  }
}

/**
 * POLICY SHAPE (what UI expects)
 * {
 *   kind: "privacy" | "terms",
 *   title: string,
 *   updatedAt: string,
 *   intro: string,
 *   sections: [
 *     {
 *       id: string,
 *       heading: string,
 *       body: string[],    // paragraphs
 *       bullets?: string[] // optional list
 *     }
 *   ],
 *   contactEmail?: string
 * }
 */

/** Default privacy policy (generic template — replace with yours) */
export const DEFAULT_PRIVACY_POLICY = {
  kind: "privacy",
  title: "Privacy Policy",
  updatedAt: "2026-01-01",
  intro:
    "This Privacy Policy explains how we collect, use, disclose, and protect information when you use our website and services.",
  contactEmail: "support@example.com",
  sections: [
    {
      id: "info-we-collect",
      heading: "Information We Collect",
      body: [
        "We may collect information you provide directly (such as name, email, and account details).",
        "We may also collect usage and device information (such as pages visited, feature usage, browser type, and approximate location).",
      ],
      bullets: [
        "Account information (e.g., name, email)",
        "Content you submit (e.g., messages, uploads)",
        "Usage data (e.g., interactions, logs)",
        "Device and technical data (e.g., OS, browser)",
      ],
    },
    {
      id: "how-we-use",
      heading: "How We Use Information",
      body: [
        "We use information to provide, maintain, and improve the service, personalize your experience, and communicate with you.",
      ],
      bullets: [
        "Provide and operate the service",
        "Improve features and performance",
        "Customer support and notices",
        "Security, fraud prevention, and compliance",
      ],
    },
    {
      id: "sharing",
      heading: "How We Share Information",
      body: [
        "We may share information with service providers who help us run the service (e.g., hosting, analytics).",
        "We may disclose information if required by law or to protect our rights and users.",
      ],
    },
    {
      id: "retention",
      heading: "Data Retention",
      body: [
        "We keep information for as long as necessary to provide the service and for legitimate business purposes, including legal compliance.",
      ],
    },
    {
      id: "your-rights",
      heading: "Your Choices and Rights",
      body: [
        "Depending on your location, you may have rights to access, correct, delete, or export your personal information.",
        "You can contact us to request changes or ask questions about this policy.",
      ],
    },
    {
      id: "security",
      heading: "Security",
      body: [
        "We use reasonable safeguards designed to protect information. However, no method of transmission or storage is 100% secure.",
      ],
    },
    {
      id: "changes",
      heading: "Changes to This Policy",
      body: [
        "We may update this policy from time to time. We will update the “Last updated” date and may notify you if changes are material.",
      ],
    },
  ],
};

/** Default terms of service (generic template — replace with yours) */
export const DEFAULT_TERMS_POLICY = {
  kind: "terms",
  title: "Terms of Service",
  updatedAt: "2026-01-01",
  intro:
    "These Terms govern your access to and use of our website and services. By using the service, you agree to these Terms.",
  contactEmail: "support@example.com",
  sections: [
    {
      id: "eligibility",
      heading: "Eligibility",
      body: [
        "You must follow applicable laws and be permitted to use the service in your jurisdiction.",
        "If you are using the service on behalf of an organization, you represent that you have authority to bind that organization.",
      ],
    },
    {
      id: "accounts",
      heading: "Accounts and Access",
      body: [
        "You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.",
      ],
      bullets: [
        "Keep your password secure",
        "Do not share login credentials",
        "Notify us of unauthorized access",
      ],
    },
    {
      id: "acceptable-use",
      heading: "Acceptable Use",
      body: [
        "You agree not to misuse the service or help anyone else do so.",
      ],
      bullets: [
        "No unlawful, harmful, or abusive activity",
        "No attempts to disrupt or reverse engineer the service",
        "No scraping or automated access unless allowed",
      ],
    },
    {
      id: "content",
      heading: "User Content",
      body: [
        "You retain rights to content you submit. You grant us a limited license to host and process that content to provide the service.",
        "You are responsible for ensuring you have rights to submit content.",
      ],
    },
    {
      id: "payments",
      heading: "Payments and Subscriptions",
      body: [
        "If you purchase a subscription, you agree to pay applicable fees. Fees may be non-refundable except where required by law.",
      ],
    },
    {
      id: "termination",
      heading: "Termination",
      body: [
        "We may suspend or terminate access if you violate these Terms or if required to protect the service or users.",
      ],
    },
    {
      id: "disclaimers",
      heading: "Disclaimers",
      body: [
        "The service is provided “as is” without warranties of any kind to the maximum extent permitted by law.",
      ],
    },
    {
      id: "limitation",
      heading: "Limitation of Liability",
      body: [
        "To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential, or punitive damages.",
      ],
    },
    {
      id: "changes",
      heading: "Changes to These Terms",
      body: [
        "We may update these Terms from time to time. We will update the “Last updated” date and may notify you if changes are material.",
      ],
    },
  ],
};
